import { useEffect } from "react";
import {
  Label,
  LangToggle,
  Ornament,
  GateVignette,
  QRPlaceholder,
} from "../components";
import { Language, APP_META } from "../constants";
import { useA11y, useStrings } from "../i18n";

interface DesktopFallbackProps {
  lang: Language;
  setLang: (lang: Language) => void;
}

export function DesktopFallback({ lang, setLang }: DesktopFallbackProps) {
  const Strings = useStrings(lang);
  const A11y = useA11y(lang);

  useEffect(() => {
    document.title = Strings.pageTitle;
  }, [Strings]);

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 font-serif grid grid-rows-[auto_1fr_auto]">
      {/* Top rail */}
      <header className="px-12 py-5 border-b border-stone-900 grid grid-cols-[1fr_auto_1fr] items-center gap-6">
        <div className="font-mono text-[11px] tracking-[0.16em] uppercase text-stone-600">
          {APP_META.title} · Vol. 01 · {APP_META.year}
        </div>
        <div className="font-display italic text-lg">
          {APP_META.location.city} · {APP_META.location.country}
        </div>
        <div className="flex justify-end">
          <LangToggle value={lang} onChange={setLang} />
        </div>
      </header>

      {/* Body */}
      <main className="grid grid-cols-[1.1fr_1fr] items-center px-12">
        <div className="pr-16 py-10">
          <Label className="text-terra-700 mb-4">{Strings.desktopNotice}</Label>
          <h1 className="font-display font-medium text-[64px] leading-[0.96] tracking-tight mb-6">
            {Strings.desktopTitle}
            <br />
          </h1>
          <p className="text-lg leading-relaxed text-stone-700 max-w-[50ch] mb-7">
            {Strings.desktopDescription}
          </p>
          <p className="text-base leading-relaxed text-stone-700 max-w-[50ch] mb-9 italic">
            {Strings.desktopNote}
          </p>

          {/* QR placeholder */}
          <div className="flex items-center gap-6">
            <div className="w-[132px] h-[132px] p-2.5 bg-white border border-stone-900">
              <QRPlaceholder title={A11y.qrCodeTitle} />
            </div>
            <div>
              <Label className="text-stone-600 mb-1.5">
                {Strings.scanWithPhone}
              </Label>
              <div className="font-mono text-sm">famagustagate.com</div>
              <div className="mt-3">
                <Ornament width={140} />
              </div>
            </div>
          </div>
        </div>

        {/* Right plate */}
        <div className="border border-stone-900 bg-stone-200 grid grid-rows-[1fr_auto]">
          <div className="px-1 py-16 flex items-center justify-center overflow-hidden">
            <GateVignette alt={A11y.gateImageAlt} />
          </div>
          <div className="border-t border-stone-900/20 px-5 py-3.5 flex justify-between items-baseline">
            <span className="font-serif italic text-sm text-stone-700">
              {Strings.plateI}
            </span>
            <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-stone-700">
              FIG. 01
            </span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-12 py-4 border-t border-stone-900 flex justify-between items-baseline font-mono text-[11px] tracking-[0.14em] uppercase text-stone-600">
        <span>
          {APP_META.location.coordinates.lat}° N ·{" "}
          {APP_META.location.coordinates.lng}° E
        </span>
        <span>{Strings.fieldGuide}</span>
        <span>
          {Strings.mobileOnly} · v {APP_META.version}
        </span>
      </footer>
    </div>
  );
}
